/**
 * Import Status Service
 * 
 * Responsible for checking import status and creating status UI tags.
 * Follows Single Responsibility Principle by handling only import status concerns.
 */

import { t } from '../i18n.js';
import { CustomPropertyService } from './custom-property-service.js';

/**
 * Builder for creating import status UI tags
 */
class ImportStatusTagBuilder {
  constructor() {
    this.importStatus = null;
    this.onViewActivityClick = null;
  }

  withImportStatus(importStatus) {
    this.importStatus = importStatus;
    return this;
  }

  withViewActivityHandler(handler) {
    this.onViewActivityClick = handler;
    return this;
  }

  build() {
    if (!this.importStatus) {
      throw new Error('Import status is required');
    }

    const statusTag = document.createElement('div');
    statusTag.className = this.importStatus.isImported 
      ? 'import-status-tag imported' 
      : 'import-status-tag not-imported';
    
    this._addIcon(statusTag);
    this._addText(statusTag);
    
    if (this.importStatus.isImported) {
      this._addActivityLink(statusTag);
      this._addTimestamp(statusTag);
    }
    
    return statusTag;
  }

  _addIcon(statusTag) {
    const icon = document.createElement('span');
    icon.className = 'import-status-icon';
    icon.textContent = this.importStatus.isImported ? '✅' : '📧';
    statusTag.appendChild(icon);
  }

  _addText(statusTag) {
    const text = document.createElement('span');
    text.className = 'import-status-text';
    text.textContent = this.importStatus.isImported ? t('already_imported') : t('not_imported_yet');
    statusTag.appendChild(text);
  }

  _addActivityLink(statusTag) {
    if (this.importStatus.activityId && this.onViewActivityClick) {
      const link = document.createElement('a');
      link.className = 'import-status-link';
      link.textContent = t('view_activity');
      link.href = '#';
      link.onclick = (e) => {
        e.preventDefault();
        this.onViewActivityClick(this.importStatus.activityId);
      };
      statusTag.appendChild(link);
    }
  }

  _addTimestamp(statusTag) {
    if (this.importStatus.importedAt) {
      const timestamp = document.createElement('span');
      timestamp.className = 'import-status-timestamp';
      const date = new Date(this.importStatus.importedAt);
      timestamp.textContent = ` ${t('imported_on')} ${date.toLocaleDateString()}`;
      statusTag.appendChild(timestamp);
    }
  }
}

/**
 * Main service for handling import status operations
 */
export class ImportStatusService {
  /**
   * Create import status tag for an Outlook item
   * 
   * @param {Object} item - Outlook item (email or appointment)
   * @param {boolean} isAppointment - Whether the item is an appointment
   * @param {Function} onViewActivityClick - Handler for viewing activity in CRM
   * @returns {HTMLElement|null} Import status tag or null
   */
  async createImportStatusTag(item, isAppointment, onViewActivityClick) {
    try {
      const importStatus = await CustomPropertyService.getImportData(item);

      if (!importStatus) {
        return new ImportStatusTagBuilder()
          .withImportStatus({ isImported: false })
          .build();
      }
      
      return new ImportStatusTagBuilder()
        .withImportStatus(importStatus)
        .withViewActivityHandler(onViewActivityClick)
        .build();
      
    } catch (error) {
      console.error('Failed to check import status:', error);
      return null;
    }
  }
}

